import { useCallback, useEffect, useState } from "react";
import {
  DialogContent,
  DialogContentText,
  Grid,
  Typography,
} from "@mui/material";

import Popup from "../../../../components/common/model/Popup";
import useStyles from "../../../../components/common/model/styles";
import FormField from "../../../../components/common/FormField";

import closeIcon from "../../../../assets/closeModel.png";
import checkedIcon from "../../../../assets/check-mark.png";
import deleteIcon from "../../../../assets/trash.png";
import Notification from "../../../../components/common/notification/Notification";
import ResetFormAlert from "../../settings/manageReffer/ResetFormAlert";
import Validation from "../../../../utils/Validation";
import {
  addSiteAction,
  initialSiteLocalState,
  setAddSiteResponse,
} from "../../../../store/reducers/storageSettingSlice";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import { useDispatch, useSelector } from "react-redux";
import { mapSelectDataToSite, Site } from "./site.type";
import { SITE_FIELDS } from "./siteFields";

interface ManageSiteProps {
  open: boolean;
  onHide: () => void;
  title: string;
  selectedForm?: any;
}

const DEFAULT_COUNTRY_CODE = "+44";

const ManageSite = ({ open, onHide, title, selectedForm }: ManageSiteProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [siteDetails, setSiteDetails] = useState<Site>(initialSiteLocalState);
  const [alerModeShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);
  const [isActionCalled, setIsActionCalled] = useState(false);
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY_CODE);
  const { isAddSiteSuccess } = useSelector(
    (state: any) => state.storageSetting,
  );
  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    siteName: siteDetails.siteName,
    address: siteDetails.address,
    postcode: siteDetails.postcode,
    siteType: siteDetails.siteType,
  });

  useEffect(() => {
    if (open) {
      setAlertModelShow(false);
      setIsClickReset(false);
      dispatch(setNotificationAction({ message: [], severity: "error" }));
    }
  }, [open]);

  // Populate the form whenever a record is selected for editing.
  useEffect(() => {
    if (selectedForm) {
      setSiteDetails(mapSelectDataToSite(selectedForm));
      setCountryCode(selectedForm.country_code || DEFAULT_COUNTRY_CODE);
    }
  }, [selectedForm]);

  // Country code is stored as its own piece of state (not part of siteDetails),
  // and kept as a plain string — it must never be merged into the phone value.
  const handleCountryCodeChange = (value: string) => {
    setCountryCode(value);
  };

  const handleInputChange = (field: string, value: any) => {
    setSiteDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    resetValidator(field);
  };

  const onBlurAction = () => {};

  const handleClear = useCallback(() => {
    setSiteDetails(
      selectedForm?.id
        ? mapSelectDataToSite(selectedForm)
        : initialSiteLocalState,
    );
    setCountryCode(selectedForm?.countryCode || DEFAULT_COUNTRY_CODE);
    resetAllValidator();
    setIsClickReset(false);
    setAlertModelShow(false);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  }, [selectedForm, resetAllValidator, dispatch]);

  const hideHandler = useCallback(() => {
    handleClear();
    onHide();
  }, [handleClear, onHide]);

  const openClearPopup = () => {
    setAlertModelShow(true);
  };

  useEffect(() => {
    if (isAddSiteSuccess) {
      dispatch(setAddSiteResponse(false));
      setIsActionCalled(false);
      hideHandler();
    }
  }, [isAddSiteSuccess]);

  useEffect(() => {
    if (isClickReset) {
      handleClear();
    }
  }, [isClickReset, handleClear]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  useEffect(() => {
    if (!isActionCalled) return;

    if (!isError() && error !== null) {
      const payload = {
        ...siteDetails,
        countryCode, // sent as its own field, kept separate from the phone number
        numberOfFloors: Number(siteDetails.numberOfFloors) || null,
        numberOfAisles: Number(siteDetails.numberOfAisles) || null,
        numberOfRowsPerAisle: Number(siteDetails.numberOfRowsPerAisle) || null,
        numberOfBaysPerRow: Number(siteDetails.numberOfBaysPerRow) || null,
        numberOfUnits: Number(siteDetails.numberOfUnits) || null,
      };
      dispatch(addSiteAction(payload));
    }
    setIsActionCalled(false);
  }, [isActionCalled, isError, error, siteDetails, countryCode, dispatch]);

  const renderFieldSection = (section: string) => {
    const fields = SITE_FIELDS.filter((field) => field.section === section);

    return (
      <Grid
        container
        spacing={0.5}
        sx={{
          border: "1px solid #f3f3f3",
          background: section === "layoutMapping" ? "#F4FBF9" : "#f4f4f4",
          p: 2,
          borderRadius: "5px",
          mt: 1,
        }}
      >
        {fields.map((field) => (
          <FormField
            key={field.id}
            {...field}
            inSingleGridForm
            isNonMarginTop
            value={siteDetails[field.id as keyof Site] ?? ""}
            error={!!error?.[field?.id]}
            InputProps={{
              ...(field?.inputProps && field?.inputProps),
              sx: { height: "36px" },
            }}
            onBlur={onBlurAction}
            countryCode={field.type === "contact" ? countryCode : undefined}
            onCountryCodeChange={
              field.type === "contact"
                ? (val: string) => handleCountryCodeChange(val)
                : undefined
            }
            onChange={(e: any) => handleInputChange(field.id, e.target.value)}
          />
        ))}
      </Grid>
    );
  };

  return (
    <>
      <Popup
        open={open}
        title={title}
        onHide={hideHandler}
        fullWidth
        maxWidth="md"
        popupBg="#fff"
        aria-describedby="alert-dialog-slide-description"
        header={
          <>
            <img
              src={checkedIcon}
              alt="save"
              className={classes.checkicons}
              onClick={(e: any) => handleSubmit(e)}
            />

            <img
              src={deleteIcon}
              alt="delete"
              className={classes.deleteicons}
              onClick={openClearPopup}
            />

            <img
              src={closeIcon}
              alt="close"
              className={classes.closeicons}
              onClick={hideHandler}
            />
          </>
        }
      >
        <DialogContent sx={{ p: "16px 10px !important" }}>
          <DialogContentText component="div">
            <Notification />
            <div style={{ display: "flex", gap: "24px" }}>
              <div style={{ flex: 1 }}>
                <h4>Site Details</h4>
                {renderFieldSection("siteDetails")}

                <h4>Floors &amp; Layout Mapping</h4>
                <Typography
                  sx={{ fontSize: "13px", color: "#6b6b6b", mb: 0.5 }}
                >
                  Walk down an <b>aisle</b>, look at a <b>row</b>, and count the{" "}
                  <b>bays</b> from left to right.
                </Typography>
                {renderFieldSection("layoutMapping")}
              </div>
            </div>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {alerModeShow && (
        <ResetFormAlert
          open={alerModeShow}
          title="Alert"
          alertModalShow={alerModeShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
    </>
  );
};

export default ManageSite;
